import React from 'react';
import { Container, Grid, Box, Typography, Card, CardContent } from '@mui/material';
import { Forum, Assessment, Home, CalendarMonth } from '@mui/icons-material';

export const ParentsSection: React.FC = () => {
    const parentFeatures = [
        {
            icon: Forum,
            title: 'Regular Communication',
            description: 'Weekly updates on progress, strengths, and areas for improvement',
        },
        {
            icon: Assessment,
            title: 'Detailed Reports',
            description: 'Comprehensive assessment results and personalized feedback',
        },
        {
            icon: Home,
            title: 'Home Practice Guidance',
            description: 'Structured homework plans and practice materials provided',
        },
        {
            icon: CalendarMonth,
            title: 'Parent Meetings',
            description: 'Scheduled consultations to discuss your child\'s journey',
        },
    ];

    return (
        <Box
            id="parents"
            sx={{
                py: { xs: 8, md: 12 },
            }}
        >
            <Container maxWidth="lg">
                <Typography
                    variant="h2"
                    sx={{
                        mb: 3,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #5b4cdb 0%, #1e293b 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    For Parents
                </Typography>

                <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center', mb: 6 }}>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.8 }}>
                        We believe parental involvement is key to success. We provide clear communication,
                        honest updates, and guidance on home practice — especially during exam season.
                    </Typography>

                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                        <strong>Your child's success is a partnership</strong> between parents, students, and
                        Myl.
                    </Typography>
                </Box>

                {/* New Parent Features Grid */}
                <Grid container spacing={3}>
                    {parentFeatures.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <Grid size={{ xs: 12, sm: 6 }} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 2,
                                                    background: 'linear-gradient(135deg, #5b4cdb 0%, #06b6d4 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Icon sx={{ color: '#fff', fontSize: 24 }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                                    {feature.title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                                    {feature.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Additional Info Box */}
                <Box
                    sx={{
                        mt: 6,
                        p: 4,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(91, 76, 219, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)',
                        border: '1px solid rgba(91, 76, 219, 0.15)',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        We're Here to Support You
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                        Have questions or concerns? We encourage open dialogue. Contact us anytime to discuss
                        your child's progress, challenges, or goals. Together, we'll ensure they reach their
                        full potential.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};
